import { useCallback, useEffect, useState } from 'react';

import { getMyNotifications } from '../../api/notificationsApi';
import { contarNaoLidasPorTela } from '../../shared/notificacoes-por-tela';

const INTERVALO_MS = 30_000;

/**
 * Quantas notificações não lidas cada tela tem (at-108) — o número do badge da Sidebar.
 *
 * ⚠️ Lê uma PÁGINA de 100 e conta as não lidas dela, porque o backend só expõe o total geral
 * (`unread-count`), sem quebra por tipo. Acima de 100 não lidas acumuladas o badge mostra menos
 * do que existe — nunca mais. Preferido a inventar um resto sem destino; se virar problema real,
 * o certo é o backend devolver a contagem agrupada.
 *
 * ⚠️ Sem toast em caso de falha: este hook alimenta um contador de menu, que acompanha toda a
 * navegação. O aviso de erro de notificação é do SINO, dentro da tela (regra da at-60 — quem
 * afirma "não há avisos" é a lista, não o número).
 */
export function useNotificacoesPorTela(
  alcancaPath: (path: string) => boolean,
  habilitado: boolean,
): Record<string, number> {
  const [porTela, setPorTela] = useState<Record<string, number>>({});

  const buscar = useCallback(async () => {
    try {
      const dados = await getMyNotifications(1, 100);
      setPorTela(contarNaoLidasPorTela(dados.items, alcancaPath));
    } catch {
      // Silêncio deliberado: ver o comentário do cabeçalho.
    }
  }, [alcancaPath]);

  useEffect(() => {
    if (!habilitado) return;
    void buscar();
    const intervalo = setInterval(() => void buscar(), INTERVALO_MS);
    return () => clearInterval(intervalo);
  }, [buscar, habilitado]);

  /**
   * O mesmo canal que o sino usa: `hooks/useCarStatusSocket.ts` emite `notification:created`
   * quando o websocket traz um aviso novo, e `notificacao:lida` é emitido pelo próprio sino ao
   * marcar lida — sem isso o número do menu só cairia no ciclo seguinte, até 30s depois de o
   * usuário ter acabado de ler.
   */
  useEffect(() => {
    if (!habilitado) return;
    const aoMudar = () => void buscar();
    window.addEventListener('notification:created', aoMudar);
    window.addEventListener('notificacao:lida', aoMudar);
    return () => {
      window.removeEventListener('notification:created', aoMudar);
      window.removeEventListener('notificacao:lida', aoMudar);
    };
  }, [buscar, habilitado]);

  return porTela;
}
